/**
 * GET /v1/me for the client launch: who is looking (staff or a client team), which studios are open, whether Beatly
 * paused every client, and this month's caps. Same cache key as the staff bar and useStaffState (['cowork', 'me']).
 *
 * A client's answer is asked again every minute (and on focus), so a studio switched on or off in the BO, or the
 * pause switch, reaches an open app within a minute. An answer once given is kept through a failed refetch (TanStack
 * keeps the data beside the error), so one failed poll never changes who is looking.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { coworkApi, type CoworkMe } from '@/cowork/omniad/api/coworkApi';
import { useAuth } from '@/contexts/AuthContext';
import { capFor, type CapView } from './caps';

export const COWORK_ME_KEY = ['cowork', 'me'] as const;
export const CLIENT_ME_REFRESH_MS = 60_000;

export function useCoworkMe() {
  const { user } = useAuth();
  return useQuery<CoworkMe>({
    queryKey: COWORK_ME_KEY,
    queryFn: () => coworkApi.staffScope.me(),
    enabled: !!user,
    // Two quick retries, so one dropped request at sign-in does not leave the session without an answer.
    retry: 2,
    retryDelay: (n) => 500 * (n + 1),
    staleTime: CLIENT_ME_REFRESH_MS,
    // Clients every minute (BO switches reach an open app); also while /v1/me has never answered, so a session that
    // started during a blip gets its real answer. Staff once answered: no polling.
    refetchInterval: (q) => (q.state.data?.staff === true ? false : CLIENT_ME_REFRESH_MS),
    refetchOnWindowFocus: true,
  });
}

/** Ask /v1/me again now (after an action that counts towards a cap, or a closed/paused answer). */
export function useRefreshMe(): () => void {
  const qc = useQueryClient();
  return useCallback(() => { void qc.invalidateQueries({ queryKey: COWORK_ME_KEY }); }, [qc]);
}

/**
 * One cap of this session's team this month, e.g. useCap('deck', 'decks') → { limit: 30, used: 12, remaining: 18, … }.
 * `null` for staff (never capped), while /v1/me loads, or when the API sends no such cap.
 */
export function useCap(studio: string, unit: string): CapView | null {
  const me = useCoworkMe();
  if (!me.data || me.data.staff) return null;
  return capFor(me.data.caps, studio, unit);
}
